import {
    AlgoliaMapBounds,
    AuctionNearMeHit,
    AuctionsNearMeRouteParams,
} from '@/pages/AuctionsNearMe/types/AuctionsNearMe.types';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import {
    defaultAuctionsNearMeUkBounds,
    defaultAuctionsNearMeUsaBounds,
    defaultHitsPerPage,
} from '@/pages/AuctionsNearMe/constants/AuctionsNearMeConstants';
import { fetchCatalogAdIfNeeded } from '@/redux/modules/catalog/ad/catalogAd.actions';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { fetchGeoAreaIfNeeded, loadAllGeoAreas } from '@/redux/modules/geoArea/geoArea';
import { FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE } from '@/pages/AuctionsNearMe/constants/featureFlags';
import { findGeoAreaFromUrlParams } from '@/pages/AuctionsNearMe/helpers/geoAreaToUrlParams';
import { getAlgoliaMapFilters } from '@/pages/AuctionsNearMe/helpers/getAlgoliaMapFilters';
import { populateAuctionsNearMeHits } from '@/redux/modules/auctionsNearMe/auctionsNearMe.reducer';
import algoliasearch from 'algoliasearch';

const auctionsNearMePageData = async ({ match, store }: DataLoaderParams<AuctionsNearMeRouteParams>) => {
    const geoAreaId = Number(match.params.geoAreaId || 0);
    const { dispatch, getState } = store;

    let mapBounds: AlgoliaMapBounds =
        getState().intl.locale.toLowerCase() === 'en-gb'
            ? defaultAuctionsNearMeUkBounds
            : defaultAuctionsNearMeUsaBounds;

    // Load all geo areas first (needed for both old and new URL formats)
    await dispatch(loadAllGeoAreas());

    // Check if using new URL format
    const hasNewUrlParams =
        ('city' in match.params && match.params.city) ||
        ('country' in match.params && match.params.country) ||
        ('state' in match.params && match.params.state);

    const isNewUrlFormat = FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE && hasNewUrlParams;

    if (isNewUrlFormat) {
        // New URL format: find geoArea by matching URL params
        const allGeoAreas = Object.values(getState().geoArea.byId);

        const geoArea = findGeoAreaFromUrlParams(allGeoAreas, {
            city: match.params.city,
            country: match.params.country,
            state: match.params.state,
        });

        if (geoArea) {
            mapBounds = {
                northEast: {
                    lat: geoArea.northEastLatitude,
                    lng: geoArea.northEastLongitude,
                },
                southWest: {
                    lat: geoArea.southWestLatitude,
                    lng: geoArea.southWestLongitude,
                },
            };
        }
    } else {
        // Old URL format: fetch geoArea by ID
        await dispatch(fetchGeoAreaIfNeeded(geoAreaId));
        if (geoAreaId) {
            const geoArea = getState().geoArea.byId[geoAreaId];
            if (geoArea) {
                mapBounds = {
                    northEast: {
                        lat: geoArea.northEastLatitude,
                        lng: geoArea.northEastLongitude,
                    },
                    southWest: {
                        lat: geoArea.southWestLatitude,
                        lng: geoArea.southWestLongitude,
                    },
                };
            }
        }
    }

    const indexName = 'Available_Catalogs';
    const { algoliaApiKey, algoliaAppId } = getState().config;
    const client = algoliasearch(algoliaAppId, algoliaApiKey);
    const index = client.initIndex(indexName);
    const { hits } = await index.search<AuctionNearMeHit>('', {
        filters: getAlgoliaMapFilters(),
        hitsPerPage: defaultHitsPerPage,
        insideBoundingBox: [
            [mapBounds.northEast.lat, mapBounds.northEast.lng, mapBounds.southWest.lat, mapBounds.southWest.lng],
        ],
    });
    const catalogIds = hits.map(({ Catalog }) => Catalog.ID);
    await Promise.all([
        dispatch(populateAuctionsNearMeHits(hits)),
        dispatch(fetchCatalogsIfNeeded({ catalogIds })),
        dispatch(fetchCatalogAdIfNeeded({ pageName: 'leaderboard-auctions-near-me' })),
    ]);
};

export default auctionsNearMePageData;
